import { type FC, lazy, memo, useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { NetworkStatus } from '@apollo/client';
import { useCreateChat, useModels, useReadChat, useSubscribeChat } from '@hooks/apollo/chat';
import { ChatBackground } from './background';
import { ChatMessage } from './message';
import { SettingsModal } from './settings';
import { ChatHeader } from './header';
import type { Message, Model } from 'src/gql/graphql';
import { useMembers } from '@hooks/apollo/members';

const SimpleMarkdownEditor = lazy(() => import('@components/prompt'));

interface KawaiiChatProps {
  chatId?: number | null;
  setChatId?: (id: number) => void;
}

const MODEL_ID_STORAGE_KEY = 'selectedModelId';

const usePersistentModelSelection = (
  availableModels: Model[],
): [string, (modelId: string) => void] => {
  const [selectedModelId, setSelectedModelIdState] = useState<string | undefined>(() => {
    const storedModelId = sessionStorage.getItem(MODEL_ID_STORAGE_KEY);
    console.log(
      'usePersistentModelSelection: Initializing state from storage:',
      storedModelId,
      'ze~!',
    );
    return storedModelId ?? availableModels?.[0]?.modelId;
  });

  useEffect(() => {
    if (!selectedModelId && availableModels && availableModels.length > 0) {
      const firstModelId = availableModels?.[0]?.modelId;
      const storedValue = sessionStorage.getItem(MODEL_ID_STORAGE_KEY);
      const storedValueIsValid =
        storedValue && availableModels.some((m) => m.modelId === storedValue);

      if (!storedValue || !storedValueIsValid) {
        console.log('usePersistentModelSelection: Setting default model:', firstModelId, 'ze~!');
        setSelectedModelIdState(firstModelId);
        sessionStorage.setItem(MODEL_ID_STORAGE_KEY, firstModelId);
      } else if (storedValue && storedValueIsValid && !selectedModelId) {
        console.log(
          'usePersistentModelSelection: Restoring valid stored model:',
          storedValue,
          'ze~!',
        );
        setSelectedModelIdState(storedValue);
      }
    }
  }, [selectedModelId, availableModels]);

  const setSelectedModelId = useCallback((modelId: string) => {
    console.log('usePersistentModelSelection: Setting and persisting model:', modelId, 'ze~!');
    setSelectedModelIdState(modelId);
    sessionStorage.setItem(MODEL_ID_STORAGE_KEY, modelId);
  }, []);

  return [selectedModelId!, setSelectedModelId];
};

const KawaiiChat: FC<KawaiiChatProps> = ({ chatId, setChatId }) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { models } = useModels();
  const { members, loading } = useMembers();
  const [memberId, setMemberId] = useState(members?.[0]?.id);
  const bottomRef = useRef();

  const { createChat } = useCreateChat();

  const { chat, networkStatus, error } = useReadChat(chatId);
  const [modelId, setModelId] = usePersistentModelSelection(models);

  useEffect(() => {
    if (!loading && !memberId) {
      setMemberId(members[0]?.id);
    }
  }, [loading]);

  useEffect(() => {
    if (chat?.modelId) {
      setModelId(chat?.modelId);
    }
    console.log('model chat', chat?.modelId);
  }, [chat]);

  const {
    messages,
    sendMessage,
    sending: sendingMessage,
    sendError,
  } = useSubscribeChat({
    chatId: chat?.id,
    initialMessages: chat?.messages as Message[],
  });

  const handleSubmit = async (input: string) => {
    const model = models.find((el) => el.modelId === modelId);
    if (sendingMessage || !input || !model) {
      console.log('Cannot send message. input empty, or already sending, ze~!', {
        chatId,
        input,
        sendingMessage,
      });
      return;
    }
    sessionStorage.removeItem(`prompt-chat-${chatId ?? 'default'}`);

    if (!chat || !chatId) {
      const chat = await createChat(input, modelId, model.provider, memberId);
      if (chat?.id && setChatId) {
        setChatId(chat.id);
      }
      return;
    }

    console.log(`Sending message to chat ${chatId}, ze~!`);
    try {
      await sendMessage({
        chatId: chatId,
        prompt: input,
      });
    } catch (err) {
      console.error('Error sending message from component, ze~!', err);
    }
  };

  const isLoading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables;

  //useEffect(() => {
  //  bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  //  console.log('scroll');
  //}, [messages.length, isLoading]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-pink-500" size={48} />
        <p className="ml-4 text-lg dark:text-white">Loading chat magic, ze~!</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        <p>Blast it! Failed to load chat: {error.message}, ze~!</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col dark:text-white overflow-hidden h-full self-end w-full bg-pink-50 dark:bg-gray-900 relative">
      <ChatBackground />

      <div
        key={chatId}
        ref={messagesContainerRef}
        className="flex-1 flex-col-reverse scrollbar-thin-bg last:grow overflow-y-auto flex  2xl:px-[15%] m-auto h-full w-full relative"
      >
        <div className="bg-transparent z-[100] bottom-[-80px] hover:bottom-0 focus-within:bottom-0 transition-all sticky w-full ">
          <SimpleMarkdownEditor
            chatId={chatId}
            onSubmit={handleSubmit}
            placeholder="Type your message!"
          />
        </div>
        <div ref={bottomRef} className="flex-grow" />
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}
        {sendError && (
          <p className="text-red-500 text-center text-sm">
            Whoops! Failed to send message, ze~! ({sendError.message})
          </p>
        )}
        <ChatHeader
          setModel={setModelId}
          member={memberId}
          model={modelId}
          setMember={setMemberId}
          onOpenSettings={() => setIsSettingsOpen(true)}
        />
      </div>

      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </div>
  );
};

export default memo(KawaiiChat);
